import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';
import StorageService from '../../utils/storageService';
import SocialIconForm from './SocialIconForm';
import SocialIconList from './SocialIconList';

const FooterManagement = () => {
  const [icons, setIcons] = useState([]);
  const [saved, setSaved] = useState(false);

  // Load icons from localStorage
  useEffect(() => {
    loadIcons();
  }, []);

  const loadIcons = () => {
    const storedIcons = StorageService.getFooterIcons();
    setIcons(storedIcons);
  };

  const handleAddIcon = (iconData) => {
    const newIcon = StorageService.addIcon(iconData);
    setIcons([...icons, newIcon]);
    showSavedNotification();
  };

  const handleUpdateIcon = (id, updatedData) => {
    StorageService.updateIcon(id, updatedData);
    loadIcons();
    showSavedNotification();
  };

  const handleDeleteIcon = (id) => {
    if (window.confirm('Are you sure you want to delete this icon?')) {
      StorageService.deleteIcon(id);
      loadIcons();
      showSavedNotification();
    }
  };

  const showSavedNotification = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Footer Management</h1>
          <p className="text-gray-400">Manage social media icons in your website footer</p>
        </div>
        
        {saved && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500 rounded-lg text-green-400"
          >
            <FaCheckCircle />
            <span className="font-medium">Saved!</span>
          </motion.div>
        )}
      </div>

      {/* Add Icon Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0a0a1f] border border-gray-800 rounded-2xl p-6"
      >
        <h2 className="text-xl font-bold text-white mb-4">Add New Social Icon</h2>
        <SocialIconForm onAdd={handleAddIcon} />
      </motion.div>

      {/* Icon List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#0a0a1f] border border-gray-800 rounded-2xl p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Current Social Icons</h2>
          <span className="text-gray-400 text-sm">{icons.length} icon{icons.length !== 1 ? 's' : ''}</span>
        </div>
        <SocialIconList
          icons={icons}
          onUpdate={handleUpdateIcon}
          onDelete={handleDeleteIcon}
        />
      </motion.div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-500/10 border border-blue-500/50 rounded-xl p-4"
      >
        <p className="text-blue-400 text-sm">
          <strong>Note:</strong> All changes are saved automatically and will appear in your website footer immediately.
          Icons are stored in your browser's local storage.
        </p>
      </motion.div>
    </div>
  );
};

export default FooterManagement;
